import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'

import { EmptyFn, EmptyPromiseFn } from 'src/utils/interfaces'
import {
  useMessagingHubData,
  UseMessagingHubData,
} from 'src/contexts/MhContext/hooks'
import useConversationsListContext from 'src/contexts/ConversationsListContext'
import { UseWAFLayout, useWAFLayout } from 'src/hooks/useWAFLayout'
import { isPublicReview } from '../ConversationsListContext/types'

interface MHContextInterface extends UseWAFLayout, UseMessagingHubData {
  isSidedrawerOpen: boolean
}

const MhContext = createContext<MHContextInterface>({
  segments: [],
  // UseMessagingHubLayout
  contentValidColWidth: 6,
  sidebarValidColWidth: 6,
  isDesktop: false,
  isMediumDesktop: false,
  isLargeDesktop: false,
  isExtraLargeDesktop: false,

  //UseMessagingHubData
  conversationsListIsLoading: true,
  isListTruncated: false,
  conversationsList: [],
  openConversationId: undefined,
  isZeroState: false,
  loadingConversationContactDetails: false,
  openConversationContactDetails: {},
  loadingConversationMessages: false,
  isOpenConversationChangeInFlight: false,
  messagesList: [],
  infoEvents: [],
  isUnreplyable: false,
  docTypeToReply: '',
  isFeedbackReviewUnreachable: false,
  conversationListFilter: '',
  setConversationListFilter: EmptyFn,
  handleSendMessage: EmptyPromiseFn,
  getOpenConversationContactDetails: EmptyPromiseFn,
  setOpenConversationId: EmptyFn,
  setIsOpenConversationChangeInFlight: EmptyFn,
  refetchConversationsList: EmptyPromiseFn,

  // Context-Provided values
  mutateConversationArchived: EmptyFn,
  mutateConversationRead: EmptyFn,

  // Control side contact pane open/close
  isSidedrawerOpen: true,
})

export const MhContextProvider: React.FCWithChildren = ({ children }) => {
  const { search } = useLocation()
  const layout = useWAFLayout()
  const data = useMessagingHubData()
  const { getConversationListItem } = useConversationsListContext()

  const [isSidedrawerManuallyOpen, setIsSidedrawerManuallyOpen] = useState(
    layout.isDesktop
  )

  /**
   * The side drawer should be forced to close if the currently open
   * conversation is the zero state or if it is an unconfirmed review
   */
  const isSidedrawerOpen = useMemo(() => {
    const { openConversationId } = data

    if (!openConversationId) {
      return false
    }

    const openConversation = getConversationListItem(openConversationId)

    if (openConversation && isPublicReview(openConversation)) {
      return false
    }

    return isSidedrawerManuallyOpen
  }, [data, getConversationListItem, isSidedrawerManuallyOpen])

  useEffect(() => {
    const searchParams = new URLSearchParams(search)

    if (searchParams.get('open-contact-profile')) {
      setIsSidedrawerManuallyOpen(
        searchParams.get('open-contact-profile') === 'true'
      )
    }
  }, [search])

  return (
    <MhContext.Provider
      value={{
        ...layout,
        ...data,
        isSidedrawerOpen,
      }}
    >
      {children}
    </MhContext.Provider>
  )
}

/**
 * Use this hook to use data and methods to manipulate
 */
const useMhContext = () => useContext(MhContext)

export default useMhContext
